
import React, {
  useEffect,
  useState,
  useRef,
} from "react"

import PulseLoader from "react-spinners/PulseLoader"

import {
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"

import {
  useGetOrgIntentionCandidateReviewMatrixQuery,
  useCreateOrUpdateOrgIntentionCandidateReviewMutation,
  useConfirmOrgIntentionCandidateDecisionMutation,
  useFinalizeOrgIntentionCandidateDecisionMutation,
  useDeleteOrgIntentionCandidateReviewMutation,
} from "@/features/orgIntentionBoards/orgIntentionCandidateReviewsApiSlice"

import useAuth from "@/hooks/useAuth"

import EtOrgIntentionCandidateCommunications
  from "@/features/orgIntentionBoards/EtOrgIntentionCandidateCommunications"


const EtOrgIntentionCandidateReviewBoard = ({
  intentionBoardId,
}) => {

  // First Created - 07 Sep 2026
  // Author - Atul Pandey
  // Purpose - Internal Trinity Intention Board candidate review
  //           and reviewer decision workspace.
  //
  // Updated - 09 Sep 2026 - Reviewer Name and Role mapping changes
  // Updated - 10 Sep 2026 - Summary cards and candidate sorting
  // Updated - 14 Sep 2026 - Independent candidate scrolling
  // Updated - 15 Sep 2026 - Email communication
  // Updated - 17 Sep 2026 - Separated decision confirmation,
  //                         lifecycle finalization and communications
  // Updated - 18 Sep 2026 - Submission date sorting
  // Updated - 18 Sep 2026 - Communications component
  // Updated - 29 Sep 2026 - Candidate search by name or email


  /*
  |--------------------------------------------------------------------------
  | 01 - Selected Candidate
  |--------------------------------------------------------------------------
  */

  const [
    selectedCandidateId,
    setSelectedCandidateId,
  ] = useState(null)


  /*
  |--------------------------------------------------------------------------
  | Workspace Tab
  |--------------------------------------------------------------------------
  */

  const [
    activeWorkspaceTab,
    setActiveWorkspaceTab,
  ] = useState("reviews")


  /*
  |--------------------------------------------------------------------------
  | Candidate Search - 29 Sep 2026
  |--------------------------------------------------------------------------
  */

  const [
    candidateSearch,
    setCandidateSearch,
  ] = useState("")


  /*
  |--------------------------------------------------------------------------
  | Candidate Review Workspace Ref
  |--------------------------------------------------------------------------
  */

  const candidateReviewPaneRef =
    useRef(null)


  /*
  |--------------------------------------------------------------------------
  | 02 - Review Form
  |--------------------------------------------------------------------------
  */

  const [
    reviewDecision,
    setReviewDecision,
  ] = useState("pending")

  const [
    reviewNotes,
    setReviewNotes,
  ] = useState("")


  /*
  |--------------------------------------------------------------------------
  | Final Decision
  |--------------------------------------------------------------------------
  */

  const [
    finalDecision,
    setFinalDecision,
  ] = useState("pending")


  /*
  |--------------------------------------------------------------------------
  | Authorization
  |--------------------------------------------------------------------------
  */

  const {
    isEtAdmin,
    isEtCoach,
    isSponsor,
  } = useAuth()


  const canFinalizeDecision =
    isEtAdmin ||
    isEtCoach ||
    isSponsor


  /*
  |--------------------------------------------------------------------------
  | 03 - Review Matrix
  |--------------------------------------------------------------------------
  */

  const {
    data: reviewMatrixData,
    isLoading: isLoadingMatrix,
    isFetching: isFetchingMatrix,
    isError: isMatrixError,
    error: matrixError,
  } = useGetOrgIntentionCandidateReviewMatrixQuery(
    {
      intentionBoardId,
    },
    {
      skip: !intentionBoardId,
    }
  )


  /*
  |--------------------------------------------------------------------------
  | 04 - Create / Update Review
  |--------------------------------------------------------------------------
  */

  const [
    createOrUpdateOrgIntentionCandidateReview,
    {
      isLoading: isSavingReview,
      isSuccess: isSaveReviewSuccess,
      isError: isSaveReviewError,
      error: saveReviewError,
    },
  ] = useCreateOrUpdateOrgIntentionCandidateReviewMutation()


  /*
  |--------------------------------------------------------------------------
  | 05a - Confirm Candidate Decision
  |--------------------------------------------------------------------------
  */

  const [
    confirmOrgIntentionCandidateDecision,
    {
      isLoading: isConfirmingDecision,
      isSuccess: isConfirmDecisionSuccess,
      isError: isConfirmDecisionError,
      error: confirmDecisionError,
    },
  ] = useConfirmOrgIntentionCandidateDecisionMutation()


  /*
  |--------------------------------------------------------------------------
  | 05b - Finalize Candidate Lifecycle
  |--------------------------------------------------------------------------
  */

  const [
    finalizeOrgIntentionCandidateDecision,
    {
      isLoading: isFinalizingDecision,
      isSuccess: isFinalizeDecisionSuccess,
      isError: isFinalizeDecisionError,
      error: finalizeDecisionError,
    },
  ] = useFinalizeOrgIntentionCandidateDecisionMutation()


  /*
  |--------------------------------------------------------------------------
  | 05c - Delete Review
  |--------------------------------------------------------------------------
  */

  const [
    deleteOrgIntentionCandidateReview,
    {
      isLoading: isDeletingReview,
      isError: isDeleteReviewError,
      error: deleteReviewError,
    },
  ] = useDeleteOrgIntentionCandidateReviewMutation()


  /*
  |--------------------------------------------------------------------------
  | 06 - Derived Data
  |--------------------------------------------------------------------------
  */

  const board =
    reviewMatrixData?.board || null

  const summary =
    reviewMatrixData?.summary || {}

  const candidates =
    reviewMatrixData?.candidates || []

  const selectedCandidate =
    candidates.find(
      candidate =>
        candidate?.intentionCandidateId === selectedCandidateId
    ) || null

  const myReview =
    selectedCandidate?.myReview || null

  const orgId =
    board?.orgId || null


  /*
  |--------------------------------------------------------------------------
  | 07 - Sync Review Form
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      if (!selectedCandidate) {

        setReviewDecision("pending")
        setReviewNotes("")
        setFinalDecision("pending")

        return
      }

      setReviewDecision(
        selectedCandidate?.myReview?.reviewDecision || "pending"
      )

      setReviewNotes(
        selectedCandidate?.myReview?.reviewNotes || ""
      )

      /*
      |--------------------------------------------------------------------------
      | Persisted Formal Decision Takes Precedence
      |--------------------------------------------------------------------------
      */

      if (
        selectedCandidate?.finalDecision &&
        selectedCandidate.finalDecision !== "pending"
      ) {

        setFinalDecision(
          selectedCandidate.finalDecision
        )

        return
      }

      const completedReviewDecisions =
        (selectedCandidate?.reviews || [])
          .filter(
            review =>
              review?.reviewDecision &&
              review.reviewDecision !== "pending"
          )
          .map(
            review => review.reviewDecision
          )

      const uniqueCompletedReviewDecisions =
        [...new Set(completedReviewDecisions)]

      if (
        uniqueCompletedReviewDecisions.length === 1
      ) {

        setFinalDecision(
          uniqueCompletedReviewDecisions[0]
        )

      } else {

        setFinalDecision("pending")

      }

    },
    [selectedCandidate]
  )


  /*
  |--------------------------------------------------------------------------
  | Sort Candidates - 18 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Earliest submittedAt first.
  | Candidate name as fallback.
  |
  | The original sort order is retained.
  |
  */

  const sortedCandidates =
    (candidates || [])
      .slice()
      .sort(
        (a, b) => {

          const aSubmittedAt =
            a?.submittedAt
              ? new Date(a.submittedAt).getTime()
              : Number.MAX_SAFE_INTEGER

          const bSubmittedAt =
            b?.submittedAt
              ? new Date(b.submittedAt).getTime()
              : Number.MAX_SAFE_INTEGER

          if (
            aSubmittedAt !== bSubmittedAt
          ) {
            return aSubmittedAt - bSubmittedAt
          }

          return (
            a?.candidateName || ""
          ).localeCompare(
            b?.candidateName || ""
          )

        }
      )


  /*
  |--------------------------------------------------------------------------
  | Filter Candidates - 29 Sep 2026
  |--------------------------------------------------------------------------
  |
  | Search is applied after the original sorting.
  | It does not modify the selected candidate or review form.
  |
  */

  const normalizedCandidateSearch =
    candidateSearch.trim().toLowerCase()


  const filteredCandidates =
    normalizedCandidateSearch
      ? sortedCandidates.filter(
          candidate => {

            const name =
              String(candidate?.candidateName || "")
                .toLowerCase()

            const email =
              String(candidate?.candidateEmail || "")
                .toLowerCase()

            return (
              name.includes(normalizedCandidateSearch) ||
              email.includes(normalizedCandidateSearch)
            )

          }
        )
      : sortedCandidates


  /*
  |--------------------------------------------------------------------------
  | Format Date
  |--------------------------------------------------------------------------
  */

  const formatDateTime =
    value => {

      if (!value) return ""

      const date = new Date(value)

      if (
        Number.isNaN(date.getTime())
      ) {
        return ""
      }

      return date.toLocaleString(
        undefined,
        {
          dateStyle: "medium",
          timeStyle: "short",
        }
      )

    }


  /*
  |--------------------------------------------------------------------------
  | Review Decision Helpers
  |--------------------------------------------------------------------------
  */

  const getReviewDecisionLabel =
    decision => {

      switch (decision) {

        case "go":
          return "Go"

        case "no_go":
          return "No Go"

        case "next_cohort":
          return "Next Cohort"

        default:
          return "Pending"

      }

    }


  const getReviewDecisionClass =
    decision => {

      switch (decision) {

        case "go":
          return "bg-lime-100 text-lime-700"

        case "no_go":
          return "bg-red-100 text-red-700"

        case "next_cohort":
          return "bg-blue-100 text-blue-700"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }


  const getDecisionStatusLabel =
    status => {

      switch (status) {

        case "aligned":
          return "Aligned"

        case "requires_alignment":
          return "Requires Alignment"

        case "finalized":
          return "Finalized"

        default:
          return "Pending"

      }

    }


  const getDecisionStatusClass =
    status => {

      switch (status) {

        case "aligned":
          return "bg-lime-100 text-lime-700"

        case "requires_alignment":
          return "bg-orange-100 text-orange-700"

        case "finalized":
          return "bg-purple-100 text-purple-700"

        default:
          return "bg-gray-100 text-gray-600"

      }

    }


  const getFinalDecisionLabel =
    decision => {

      switch (decision) {

        case "go":
          return "Go"

        case "no_go":
          return "No Go"

        case "next_cohort":
          return "Next Cohort"

        default:
          return "Pending"

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Reviewer Cohort Role Mapping
  |--------------------------------------------------------------------------
  */

  const reviewerCohortRoleMap = {

    EtAdmin: "Cohort Coach",
    EtCoach: "Cohort Coach",
    OrgSponsor: "Org Sponsor",
    OrgFacilitator: "Org Igniter",

  }


  const getReviewerCohortRole =
    reviewerRole => {

      return (
        reviewerCohortRoleMap[reviewerRole] ||
        reviewerRole ||
        "Reviewer"
      )

    }


  /*
  |--------------------------------------------------------------------------
  | 14 - Select Candidate
  |--------------------------------------------------------------------------
  */

  const handleSelectCandidate =
    candidate => {

      if (
        !candidate?.intentionCandidateId
      ) {
        return
      }

      setSelectedCandidateId(
        candidate.intentionCandidateId
      )

      candidateReviewPaneRef.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      })

    }


  /*
  |--------------------------------------------------------------------------
  | 15 - Close Candidate
  |--------------------------------------------------------------------------
  */

  const handleCloseCandidate =
    () => {

      setSelectedCandidateId(null)

    }


  /*
  |--------------------------------------------------------------------------
  | 16 - Save Review
  |--------------------------------------------------------------------------
  */

  const handleSaveReview =
    async () => {

      if (
        !selectedCandidate?.intentionCandidateId
      ) {
        return
      }

      try {

        await createOrUpdateOrgIntentionCandidateReview({

          intentionBoardId,

          intentionCandidateId:
            selectedCandidate.intentionCandidateId,

          reviewDecision,

          reviewNotes,

        }).unwrap()

      } catch (error) {

        console.error(error)

      }

    }


  /*
  |--------------------------------------------------------------------------
  | 17 - Delete My Review
  |--------------------------------------------------------------------------
  */

  const handleDeleteReview =
    async () => {

      if (
        !myReview?.intentionCandidateReviewId
      ) {
        return
      }

      try {

        await deleteOrgIntentionCandidateReview({

          intentionCandidateReviewId:
            myReview.intentionCandidateReviewId,

          intentionBoardId,

          intentionCandidateId:
            selectedCandidate?.intentionCandidateId,

        }).unwrap()

        setReviewDecision("pending")
        setReviewNotes("")

      } catch (error) {

        console.error(error)

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Confirm Candidate Decision
  |--------------------------------------------------------------------------
  */

  const handleConfirmDecision =
    async () => {

      if (
        !selectedCandidate?.intentionCandidateId ||
        finalDecision === "pending"
      ) {
        return
      }

      const confirmed =
        window.confirm(
          `Confirm ${selectedCandidate?.candidateName || "this candidate"} as ${getFinalDecisionLabel(finalDecision)}?`
        )

      if (!confirmed) return

      try {

        await confirmOrgIntentionCandidateDecision({

          intentionBoardId,

          intentionCandidateId:
            selectedCandidate.intentionCandidateId,

          finalDecision,

        }).unwrap()

      } catch (error) {

        console.error(error)

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Finalize Candidate Lifecycle
  |--------------------------------------------------------------------------
  */

  const handleFinalizeCandidate =
    async () => {

      if (
        !selectedCandidate?.intentionCandidateId ||
        !selectedCandidate?.finalDecision ||
        selectedCandidate.finalDecision === "pending"
      ) {
        return
      }

      const confirmed =
        window.confirm(
          `Finalize the candidate lifecycle for ${selectedCandidate?.candidateName || "this candidate"}?`
        )

      if (!confirmed) return

      try {

        await finalizeOrgIntentionCandidateDecision({

          intentionBoardId,

          intentionCandidateId:
            selectedCandidate.intentionCandidateId,

        }).unwrap()

      } catch (error) {

        console.error(error)

      }

    }


  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (isLoadingMatrix) {

    return (

      <div className="bg-white rounded m-1 p-4">

        <PulseLoader
          color="#ea580c"
          size={7}
        />

      </div>

    )

  }


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div className="m-1 p-1 bg-white rounded">

      {/*
      |--------------------------------------------------------------------------
      | Header
      |--------------------------------------------------------------------------
      */}

      <div className="m-1 p-2">

        <p className="font-bold text-lg text-gray-800">
          Candidate Workspace
        </p>

        <p className="text-xs text-gray-500 mt-1">
          {board?.boardName || "Intention Board"}
        </p>

      </div>


      {/*
      |--------------------------------------------------------------------------
      | Workspace Tabs
      |--------------------------------------------------------------------------
      */}

      <div className="flex justify-start items-center gap-1 m-1 p-1 bg-gray-100 rounded">

        <button

          type="button"

          onClick={() =>
            setActiveWorkspaceTab("reviews")
          }

          className={`rounded px-4 py-2 text-[10px] font-bold ${
            activeWorkspaceTab === "reviews"
              ? "bg-white text-orange-700"
              : "text-gray-600 hover:bg-white"
          }`}

        >
          Review & Decisions
        </button>


        {isEtAdmin && (

          <button

            type="button"

            onClick={() =>
              setActiveWorkspaceTab("communications")
            }

            className={`rounded px-4 py-2 text-[10px] font-bold ${
              activeWorkspaceTab === "communications"
                ? "bg-white text-orange-700"
                : "text-gray-600 hover:bg-white"
            }`}

          >
            Communications
          </button>

        )}

      </div>


      {activeWorkspaceTab === "reviews" && (

        <>

          {/*
          |--------------------------------------------------------------------------
          | Matrix Error
          |--------------------------------------------------------------------------
          */}

          {isMatrixError && (

            <div className="bg-orange-50 rounded m-1 p-3">

              <p className="text-xs text-orange-700">

                {
                  matrixError?.data?.message ||
                  "Unable to load Intention Board review matrix."
                }

              </p>

            </div>

          )}


          {isFetchingMatrix && !isLoadingMatrix && (

            <div className="m-2">

              <PulseLoader
                color="#ea580c"
                size={5}
              />

            </div>

          )}


          {/*
          |--------------------------------------------------------------------------
          | Summary Cards
          |--------------------------------------------------------------------------
          */}

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 m-1">

            {[
              {
                label: "Submitted",
                value: summary?.submittedCandidates,
                className: "bg-gray-100",
                labelClass: "text-gray-500",
              },
              {
                label: "Reviewed By Me",
                value: summary?.candidatesReviewedByMe,
                className: "bg-lime-50",
                labelClass: "text-lime-700",
              },
              {
                label: "Awaiting My Review",
                value: summary?.candidatesNotReviewedByMe,
                className: "bg-yellow-50",
                labelClass: "text-yellow-700",
              },
              {
                label: "Aligned",
                value: summary?.aligned,
                className: "bg-lime-50",
                labelClass: "text-lime-700",
              },
              {
                label: "Requires Alignment",
                value: summary?.requiresAlignment,
                className: "bg-orange-50",
                labelClass: "text-orange-700",
              },
              {
                label: "Confirmed Go",
                value: summary?.confirmedGo,
                className: "bg-lime-50",
                labelClass: "text-lime-700",
              },
              {
                label: "Finalized",
                value: summary?.finalized,
                className: "bg-purple-50",
                labelClass: "text-purple-700",
              },
            ].map(
              item => (

                <div
                  key={item.label}
                  className={`rounded p-3 ${item.className}`}
                >

                  <p className={`text-[9px] font-bold uppercase ${item.labelClass}`}>
                    {item.label}
                  </p>

                  <p className="text-xl font-bold text-gray-800 mt-1">
                    {item.value || 0}
                  </p>

                </div>

              )
            )}

          </div>


          {/*
          |--------------------------------------------------------------------------
          | Main Layout
          |--------------------------------------------------------------------------
          */}

          <div
            className="
              grid
              grid-cols-1
              lg:grid-cols-12
              gap-1
              m-1
              lg:h-[70vh]
              lg:overflow-hidden
            "
          >

            {/*
            |--------------------------------------------------------------------------
            | LEFT - Candidate Review Queue
            |--------------------------------------------------------------------------
            */}

            <div
              className="
                lg:col-span-4
                bg-gray-200
                rounded
                p-1
                lg:h-full
                lg:min-h-0
                lg:flex
                lg:flex-col
                lg:overflow-hidden
              "
            >

              {/*
              |--------------------------------------------------------------------------
              | Submitted Candidates Header and Search - 29 Sep 2026
              |--------------------------------------------------------------------------
              */}

              <div className="bg-white rounded m-1 p-3 shrink-0">

                <p className="font-bold text-xs text-gray-800">
                  Submitted Candidates
                </p>

                <p className="text-[9px] text-gray-500 mt-1">
                  Select a candidate to review their current recommendation and alignment status.
                </p>


                {/*
                |--------------------------------------------------------------------------
                | Search Input
                |--------------------------------------------------------------------------
                */}

                <div className="relative mt-3">

                  <MagnifyingGlassIcon
                    className="
                      absolute
                      left-3
                      top-1/2
                      -translate-y-1/2
                      h-4
                      w-4
                      text-gray-400
                      pointer-events-none
                    "
                  />

                  <input

                    type="text"

                    value={candidateSearch}

                    onChange={
                      event =>
                        setCandidateSearch(
                          event.target.value
                        )
                    }

                    placeholder="Search by name or email..."

                    aria-label="Search submitted candidates by name or email"

                    className="
                      w-full
                      rounded
                      border
                      border-gray-200
                      bg-gray-50
                      pl-9
                      pr-8
                      py-2
                      text-[11px]
                      text-gray-700
                      outline-none
                      focus:border-orange-400
                    "

                  />


                  {candidateSearch && (

                    <button

                      type="button"

                      title="Clear search"

                      aria-label="Clear candidate search"

                      onClick={() =>
                        setCandidateSearch("")
                      }

                      className="
                        absolute
                        right-2
                        top-1/2
                        -translate-y-1/2
                        text-gray-400
                        hover:text-orange-600
                      "

                    >

                      <XMarkIcon className="w-4 h-4" />

                    </button>

                  )}

                </div>


                {/*
                |--------------------------------------------------------------------------
                | Visible Candidate Count
                |--------------------------------------------------------------------------
                */}

                <div className="flex justify-between items-center gap-2 mt-3">

                  <p className="text-[9px] text-gray-500">

                    {
                      normalizedCandidateSearch
                        ? "Search Results"
                        : "All Submitted Candidates"
                    }

                  </p>

                  <p className="text-[9px] font-bold text-gray-600">

                    {filteredCandidates.length}
                    {" of "}
                    {sortedCandidates.length}

                  </p>

                </div>

              </div>


              {/*
              |--------------------------------------------------------------------------
              | No Submitted Candidates
              |--------------------------------------------------------------------------
              */}

              {candidates.length === 0 && (

                <div className="bg-white rounded m-1 p-3">

                  <p className="text-xs text-gray-500">
                    There are no submitted candidates available for review.
                  </p>

                </div>

              )}


              {/*
              |--------------------------------------------------------------------------
              | Candidate List
              |--------------------------------------------------------------------------
              */}

              <div
                className="
                  lg:flex-1
                  lg:min-h-0
                  lg:overflow-y-auto
                "
              >

                {/*
                |--------------------------------------------------------------------------
                | No Search Matches
                |--------------------------------------------------------------------------
                */}

                {sortedCandidates.length > 0
                  &&
                  filteredCandidates.length === 0
                  &&
                  (

                    <div className="bg-white rounded m-1 p-4">

                      <p className="text-xs font-bold text-gray-700">
                        No matching candidates
                      </p>

                      <p className="text-[10px] text-gray-500 mt-1">
                        Try another name or email address.
                      </p>

                      <button

                        type="button"

                        onClick={() =>
                          setCandidateSearch("")
                        }

                        className="text-[10px] font-bold text-orange-600 mt-3"

                      >
                        Clear Search
                      </button>

                    </div>

                  )
                }


                {/*
                |--------------------------------------------------------------------------
                | Filtered Candidate Rows
                |--------------------------------------------------------------------------
                */}

                {filteredCandidates.map(
                  (candidate, index) => {

                    const isSelected =
                      selectedCandidateId ===
                      candidate?.intentionCandidateId

                    const candidateMyDecision =
                      candidate?.myReview?.reviewDecision ||
                      "pending"

                    return (

                      <div

                        key={
                          candidate?.intentionCandidateId ||
                          index
                        }

                        onClick={() =>
                          handleSelectCandidate(candidate)
                        }

                        className={`rounded m-1 p-3 cursor-pointer ${
                          isSelected
                            ? "bg-orange-50"
                            : "bg-white hover:bg-orange-50"
                        }`}

                      >

                        <div className="flex justify-between items-start gap-2">

                          <div className="flex justify-start items-start gap-2 flex-1 min-w-0">

                            <div className="w-6 h-6 rounded-full bg-gray-100 flex justify-center items-center shrink-0">

                              <p className="text-[9px] font-bold text-gray-600">
                                {index + 1}
                              </p>

                            </div>


                            <div className="flex-1 min-w-0">

                              <p
                                className="text-xs font-bold text-gray-800 leading-snug break-words line-clamp-2"
                                title={candidate?.candidateName}
                              >
                                {candidate?.candidateName}
                              </p>

                              <p
                                className="text-[9px] text-gray-500 mt-1 truncate"
                                title={candidate?.candidateEmail}
                              >
                                {candidate?.candidateEmail}
                              </p>

                            </div>

                          </div>


                          <p
                            className={`text-[9px] font-bold rounded-full px-2 py-1 ${
                              getDecisionStatusClass(
                                candidate?.decisionStatus
                              )
                            }`}
                          >

                            {
                              getDecisionStatusLabel(
                                candidate?.decisionStatus
                              )
                            }

                          </p>

                        </div>


                        <div className="flex justify-between items-center mt-3">

                          <div>

                            <p className="text-[9px] text-gray-500">
                              My Review
                            </p>

                            <p
                              className={`inline-block text-[9px] font-bold rounded-full px-2 py-1 mt-1 ${
                                getReviewDecisionClass(
                                  candidateMyDecision
                                )
                              }`}
                            >

                              {
                                getReviewDecisionLabel(
                                  candidateMyDecision
                                )
                              }

                            </p>

                          </div>


                          <div className="text-right">

                            <p className="text-[9px] text-gray-500">
                              Reviews
                            </p>

                            <p className="text-xs font-bold text-gray-700 mt-1">
                              {candidate?.reviewCounts?.total || 0}
                            </p>

                          </div>

                        </div>

                      </div>

                    )

                  }
                )}

              </div>

            </div>


            {/*
            |--------------------------------------------------------------------------
            | RIGHT - Candidate Review Workspace
            |--------------------------------------------------------------------------
            */}

            <div

              ref={candidateReviewPaneRef}

              className="
                lg:col-span-8
                bg-gray-200
                rounded
                p-1
                lg:h-full
                lg:min-h-0
                lg:overflow-y-auto
              "

            >

              {!selectedCandidate && (

                <div className="bg-white rounded m-1 p-6">

                  <p className="text-sm font-bold text-gray-700">
                    Candidate Review
                  </p>

                  <p className="text-xs text-gray-500 mt-2">
                    Select a submitted candidate to view reviewer recommendations and record your review.
                  </p>

                </div>

              )}


              {selectedCandidate && (

                <div>

                  {/*
                  |--------------------------------------------------------------------------
                  | Candidate Header
                  |--------------------------------------------------------------------------
                  */}

                  <div className="bg-white rounded m-1 p-4">

                    <div className="flex justify-between items-start">

                      <div>

                        <p className="text-lg font-bold text-gray-800">
                          {selectedCandidate?.candidateName}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {selectedCandidate?.candidateEmail}
                        </p>

                        {selectedCandidate?.submittedAt && (

                          <p className="text-[9px] text-gray-500 mt-2">

                            Submitted: {
                              formatDateTime(
                                selectedCandidate.submittedAt
                              )
                            }

                          </p>

                        )}

                      </div>


                      <button
                        type="button"
                        onClick={handleCloseCandidate}
                        className="text-[10px] font-bold text-orange-600"
                      >
                        Close
                      </button>

                    </div>


                    <div className="flex justify-start items-center flex-wrap mt-3">

                      <p
                        className={`text-[9px] font-bold rounded-full px-2 py-1 mr-2 ${
                          getDecisionStatusClass(
                            selectedCandidate?.decisionStatus
                          )
                        }`}
                      >

                        Review Status: {
                          getDecisionStatusLabel(
                            selectedCandidate?.decisionStatus
                          )
                        }

                      </p>


                      <p
                        className={`text-[9px] font-bold rounded-full px-2 py-1 mr-2 ${
                          getReviewDecisionClass(
                            selectedCandidate?.finalDecision
                          )
                        }`}
                      >

                        Decision: {
                          getFinalDecisionLabel(
                            selectedCandidate?.finalDecision
                          )
                        }

                      </p>


                      {selectedCandidate?.decisionStatus === "finalized" && (

                        <p className="text-[9px] font-bold rounded-full px-2 py-1 mr-2 bg-purple-100 text-purple-700">
                          Lifecycle Finalized
                        </p>

                      )}

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Review Counts
                  |--------------------------------------------------------------------------
                  */}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-1 m-1">

                    {[
                      {
                        label: "Go",
                        value: selectedCandidate?.reviewCounts?.go,
                        color: "text-lime-700",
                      },
                      {
                        label: "No Go",
                        value: selectedCandidate?.reviewCounts?.no_go,
                        color: "text-red-700",
                      },
                      {
                        label: "Next Cohort",
                        value: selectedCandidate?.reviewCounts?.next_cohort,
                        color: "text-blue-700",
                      },
                      {
                        label: "Pending",
                        value: selectedCandidate?.reviewCounts?.pending,
                        color: "text-gray-700",
                      },
                    ].map(
                      item => (

                        <div
                          key={item.label}
                          className="bg-white rounded p-3"
                        >

                          <p className="text-[9px] font-bold text-gray-500 uppercase">
                            {item.label}
                          </p>

                          <p className={`text-lg font-bold mt-1 ${item.color}`}>
                            {item.value || 0}
                          </p>

                        </div>

                      )
                    )}

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | My Review
                  |--------------------------------------------------------------------------
                  */}

                  <div className="bg-white rounded m-1 p-4">

                    <p className="text-sm font-bold text-gray-800">
                      My Review
                    </p>

                    <p className="text-[10px] text-gray-500 mt-1">
                      Record your independent recommendation for this candidate.
                    </p>


                    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-4">

                      {[
                        {
                          value: "go",
                          label: "Go",
                          active: "bg-lime-600 text-white",
                          inactive: "bg-lime-50 text-lime-700",
                        },
                        {
                          value: "no_go",
                          label: "No Go",
                          active: "bg-red-600 text-white",
                          inactive: "bg-red-50 text-red-700",
                        },
                        {
                          value: "next_cohort",
                          label: "Next Cohort",
                          active: "bg-blue-600 text-white",
                          inactive: "bg-blue-50 text-blue-700",
                        },
                        {
                          value: "pending",
                          label: "Pending",
                          active: "bg-gray-600 text-white",
                          inactive: "bg-gray-100 text-gray-600",
                        },
                      ].map(
                        item => (

                          <button

                            key={item.value}

                            type="button"

                            onClick={() =>
                              setReviewDecision(item.value)
                            }

                            className={`rounded px-3 py-3 text-xs font-bold ${
                              reviewDecision === item.value
                                ? item.active
                                : item.inactive
                            }`}

                          >

                            {item.label}

                          </button>

                        )
                      )}

                    </div>


                    <div className="mt-4">

                      <label className="block text-[10px] font-bold text-gray-600 mb-1">
                        Review Notes
                      </label>

                      <textarea

                        value={reviewNotes}

                        onChange={
                          event =>
                            setReviewNotes(event.target.value)
                        }

                        rows={5}

                        className="w-full rounded border border-gray-300 p-3 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500"

                        placeholder="Add observations, rationale or considerations..."

                      />

                    </div>


                    {isSaveReviewError && (

                      <p className="text-xs text-red-700 mt-3">
                        {
                          saveReviewError?.data?.message ||
                          "Unable to save candidate review."
                        }
                      </p>

                    )}


                    {isDeleteReviewError && (

                      <p className="text-xs text-red-700 mt-3">
                        {
                          deleteReviewError?.data?.message ||
                          "Unable to remove candidate review."
                        }
                      </p>

                    )}


                    {isSaveReviewSuccess && (

                      <p className="text-xs text-lime-700 mt-3">
                        Review saved successfully.
                      </p>

                    )}


                    <div className="flex justify-between items-center mt-4">

                      <div>

                        {myReview && (

                          <button

                            type="button"

                            disabled={
                              isDeletingReview ||
                              selectedCandidate?.decisionStatus === "finalized"
                            }

                            onClick={handleDeleteReview}

                            className="rounded bg-gray-100 text-red-700 px-3 py-2 text-[10px] font-bold disabled:opacity-50"

                          >

                            {
                              isDeletingReview
                                ? "Removing..."
                                : "Remove My Review"
                            }

                          </button>

                        )}

                      </div>


                      <button

                        type="button"

                        disabled={
                          isSavingReview ||
                          selectedCandidate?.decisionStatus === "finalized"
                        }

                        onClick={handleSaveReview}

                        className="rounded bg-orange-600 text-white px-5 py-2 text-xs font-bold disabled:opacity-50"

                      >

                        {
                          isSavingReview
                            ? "Saving..."
                            : "Save Review"
                        }

                      </button>

                    </div>

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Reviewer Recommendations
                  |--------------------------------------------------------------------------
                  */}

                  <div className="bg-white rounded m-1 p-4">

                    <p className="text-sm font-bold text-gray-800">
                      Reviewer Recommendations
                    </p>

                    <p className="text-[10px] text-gray-500 mt-1">
                      Active recommendations currently recorded for this candidate.
                    </p>


                    {selectedCandidate?.reviews?.length === 0 && (

                      <div className="bg-gray-50 rounded p-3 mt-3">

                        <p className="text-xs text-gray-500">
                          No reviewer recommendations have been recorded yet.
                        </p>

                      </div>

                    )}


                    {selectedCandidate?.reviews?.map(
                      (review, index) => (

                        <div
                          key={
                            review?.intentionCandidateReviewId ||
                            index
                          }
                          className="bg-gray-50 rounded p-3 mt-2"
                        >

                          <div className="flex justify-between items-start">

                            <div>

                              <p className="text-xs font-bold text-gray-700">
                                Reviewer {index + 1}
                              </p>

                              <p className="text-xs text-gray-600 mt-1">

                                <span className="font-bold text-gray-700">

                                  {
                                    review?.reviewerName ||
                                    "Reviewer"
                                  }

                                </span>

                                {", "}

                                {
                                  getReviewerCohortRole(
                                    review?.reviewerRole
                                  )
                                }

                              </p>

                            </div>


                            <p
                              className={`text-[9px] font-bold rounded-full px-2 py-1 ${
                                getReviewDecisionClass(
                                  review?.reviewDecision
                                )
                              }`}
                            >

                              {
                                getReviewDecisionLabel(
                                  review?.reviewDecision
                                )
                              }

                            </p>

                          </div>


                          {review?.reviewNotes && (

                            <p className="text-xs text-gray-700 mt-3 whitespace-pre-wrap leading-5">
                              {review.reviewNotes}
                            </p>

                          )}


                          {review?.reviewedAt && (

                            <p className="text-[9px] text-gray-500 mt-2">

                              Reviewed: {
                                formatDateTime(review.reviewedAt)
                              }

                            </p>

                          )}

                        </div>

                      )
                    )}

                  </div>


                  {/*
                  |--------------------------------------------------------------------------
                  | Candidate Decision
                  |--------------------------------------------------------------------------
                  */}

                  {selectedCandidate?.decisionStatus === "finalized" ? (

                    /*
                    |--------------------------------------------------------------------------
                    | Finalized Candidate Lifecycle
                    |--------------------------------------------------------------------------
                    */

                    <div className="bg-white rounded m-1 p-4">

                      <div className="flex justify-between items-start">

                        <div>

                          <p className="text-sm font-bold text-gray-800">
                            Candidate Decision
                          </p>

                          <p className="text-[10px] text-gray-500 mt-1">
                            This candidate lifecycle has been formally finalized.
                          </p>

                        </div>

                        <p className="text-[9px] font-bold rounded-full px-3 py-1 bg-purple-100 text-purple-700">
                          Finalized
                        </p>

                      </div>


                      <div className="bg-gray-50 rounded p-4 mt-4">

                        <p className="text-[9px] font-bold text-gray-500 uppercase">
                          Confirmed Decision
                        </p>

                        <p
                          className={`inline-block text-xs font-bold rounded-full px-3 py-2 mt-2 ${
                            getReviewDecisionClass(
                              selectedCandidate?.finalDecision
                            )
                          }`}
                        >

                          {
                            getFinalDecisionLabel(
                              selectedCandidate?.finalDecision
                            )
                          }

                        </p>


                        {selectedCandidate?.decisionAt && (

                          <p className="text-[9px] text-gray-500 mt-3">

                            Decision confirmed: {
                              formatDateTime(
                                selectedCandidate.decisionAt
                              )
                            }

                          </p>

                        )}


                        {selectedCandidate?.finalizedAt && (

                          <p className="text-[9px] text-gray-500 mt-1">

                            Lifecycle finalized: {
                              formatDateTime(
                                selectedCandidate.finalizedAt
                              )
                            }

                          </p>

                        )}

                      </div>

                    </div>

                  ) : (

                    canFinalizeDecision && (

                      selectedCandidate?.finalDecision &&
                      selectedCandidate.finalDecision !== "pending" ? (

                        /*
                        |--------------------------------------------------------------------------
                        | Confirmed Decision
                        |--------------------------------------------------------------------------
                        */

                        <div className="bg-white rounded m-1 p-4">

                          <div className="flex justify-between items-start gap-3">

                            <div>

                              <p className="text-sm font-bold text-gray-800">
                                Candidate Decision
                              </p>

                              <p className="text-[10px] text-gray-500 mt-1">
                                The formal candidate decision has been confirmed. The candidate lifecycle remains open until it is finalized.
                              </p>

                            </div>

                            <p className="text-[9px] font-bold rounded-full px-3 py-1 bg-lime-100 text-lime-700">
                              Confirmed
                            </p>

                          </div>


                          <div className="bg-gray-50 rounded p-4 mt-4">

                            <p className="text-[9px] font-bold text-gray-500 uppercase">
                              Confirmed Decision
                            </p>

                            <p
                              className={`inline-block text-xs font-bold rounded-full px-3 py-2 mt-2 ${
                                getReviewDecisionClass(
                                  selectedCandidate?.finalDecision
                                )
                              }`}
                            >

                              {
                                getFinalDecisionLabel(
                                  selectedCandidate?.finalDecision
                                )
                              }

                            </p>


                            {selectedCandidate?.decisionAt && (

                              <p className="text-[9px] text-gray-500 mt-3">

                                Decision confirmed: {
                                  formatDateTime(
                                    selectedCandidate.decisionAt
                                  )
                                }

                              </p>

                            )}

                          </div>


                          {isFinalizeDecisionError && (

                            <p className="text-xs text-red-700 mt-3">
                              {
                                finalizeDecisionError?.data?.message ||
                                "Unable to finalize candidate lifecycle."
                              }
                            </p>

                          )}


                          {isFinalizeDecisionSuccess && (

                            <p className="text-xs text-lime-700 mt-3">
                              Candidate lifecycle finalized successfully.
                            </p>

                          )}


                          <div className="flex justify-end items-center mt-4">

                            <button

                              type="button"

                              disabled={isFinalizingDecision}

                              onClick={handleFinalizeCandidate}

                              className="rounded bg-purple-600 text-white px-5 py-2 text-xs font-bold disabled:opacity-50"

                            >

                              {
                                isFinalizingDecision
                                  ? "Finalizing..."
                                  : "Finalize Candidate"
                              }

                            </button>

                          </div>

                        </div>

                      ) : (

                        /*
                        |--------------------------------------------------------------------------
                        | Decision Confirmation Workspace
                        |--------------------------------------------------------------------------
                        */

                        <div className="bg-white rounded m-1 p-4">

                          <p className="text-sm font-bold text-gray-800">
                            Candidate Decision
                          </p>

                          <p className="text-[10px] text-gray-500 mt-1">
                            Review the recommendations above and confirm the formal candidate decision.
                          </p>


                          <div className="bg-gray-50 rounded p-3 mt-4">

                            <div className="flex justify-between items-center">

                              <div>

                                <p className="text-[9px] font-bold text-gray-500 uppercase">
                                  Recommendation Status
                                </p>

                                <p
                                  className={`inline-block text-[9px] font-bold rounded-full px-2 py-1 mt-2 ${
                                    getDecisionStatusClass(
                                      selectedCandidate?.decisionStatus
                                    )
                                  }`}
                                >

                                  {
                                    getDecisionStatusLabel(
                                      selectedCandidate?.decisionStatus
                                    )
                                  }

                                </p>

                              </div>


                              <div className="text-right">

                                <p className="text-[9px] text-gray-500">
                                  Completed Reviews
                                </p>

                                <p className="text-sm font-bold text-gray-700 mt-1">

                                  {
                                    (selectedCandidate?.reviewCounts?.go || 0) +
                                    (selectedCandidate?.reviewCounts?.no_go || 0) +
                                    (selectedCandidate?.reviewCounts?.next_cohort || 0)
                                  }

                                </p>

                              </div>

                            </div>


                            {selectedCandidate?.allCompletedReviewsAligned && (

                              <p className="text-[10px] text-lime-700 mt-3">
                                Reviewer recommendations are aligned. The aligned recommendation has been preselected below.
                              </p>

                            )}


                            {selectedCandidate?.decisionStatus === "requires_alignment" && (

                              <p className="text-[10px] text-orange-700 mt-3">
                                Reviewer recommendations are not aligned. Select the formal candidate decision below.
                              </p>

                            )}

                          </div>


                          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 mt-4">

                            {[
                              {
                                value: "go",
                                label: "Go",
                                active: "bg-lime-600 text-white",
                                inactive: "bg-lime-50 text-lime-700",
                              },
                              {
                                value: "no_go",
                                label: "No Go",
                                active: "bg-red-600 text-white",
                                inactive: "bg-red-50 text-red-700",
                              },
                              {
                                value: "next_cohort",
                                label: "Next Cohort",
                                active: "bg-blue-600 text-white",
                                inactive: "bg-blue-50 text-blue-700",
                              },
                            ].map(
                              item => (

                                <button

                                  key={item.value}

                                  type="button"

                                  disabled={isConfirmingDecision}

                                  onClick={() =>
                                    setFinalDecision(item.value)
                                  }

                                  className={`rounded px-3 py-3 text-xs font-bold ${
                                    finalDecision === item.value
                                      ? item.active
                                      : item.inactive
                                  } disabled:opacity-50`}

                                >

                                  {item.label}

                                </button>

                              )
                            )}

                          </div>


                          {!selectedCandidate?.hasCompletedReviews && (

                            <p className="text-xs text-orange-700 mt-3">
                              At least one completed reviewer recommendation is required before the candidate decision can be confirmed.
                            </p>

                          )}


                          {isConfirmDecisionError && (

                            <p className="text-xs text-red-700 mt-3">
                              {
                                confirmDecisionError?.data?.message ||
                                "Unable to confirm candidate decision."
                              }
                            </p>

                          )}


                          {isConfirmDecisionSuccess && (

                            <p className="text-xs text-lime-700 mt-3">
                              Candidate decision confirmed successfully.
                            </p>

                          )}


                          <div className="flex justify-end items-center mt-4">

                            <button

                              type="button"

                              disabled={
                                isConfirmingDecision ||
                                !selectedCandidate?.hasCompletedReviews ||
                                finalDecision === "pending"
                              }

                              onClick={handleConfirmDecision}

                              className="rounded bg-orange-600 text-white px-5 py-2 text-xs font-bold disabled:opacity-50"

                            >

                              {
                                isConfirmingDecision
                                  ? "Confirming..."
                                  : "Confirm Decision"
                              }

                            </button>

                          </div>

                        </div>

                      )

                    )

                  )}

                </div>

              )}

            </div>

          </div>

        </>

      )}


      {/*
      |--------------------------------------------------------------------------
      | Communications
      |--------------------------------------------------------------------------
      */}

      {activeWorkspaceTab === "communications" &&
        isEtAdmin && (

        <EtOrgIntentionCandidateCommunications

          intentionBoardId={intentionBoardId}

          orgId={orgId}

          candidates={sortedCandidates}

        />

      )}

    </div>

  )

}


export default EtOrgIntentionCandidateReviewBoard